import { apiRuntime, CSRF_HEADER, CSRF_HEADER_VALUE } from '@web/core/http/api-config';

/**
 * Renewing the session (`POST /api/auth/refresh`): the API rotates the refresh cookie and
 * sets a new access cookie. Rules:
 *
 * - ONE rotation at a time: concurrent callers in a tab share the same promise, and tabs
 *   take turns through `navigator.locks`. A tab that waited while another one rotated does
 *   not rotate again: the cookies it will send are already new.
 * - `invalid` (400/401/403): the session is over — sign in again.
 * - `unavailable` (network, 429, 5xx): the session may be fine — keep it, show the error.
 */
export type RefreshOutcome = 'ok' | 'invalid' | 'unavailable';

const REFRESH_LOCK = 'crm-session-refresh';
const LAST_REFRESH_KEY = 'crm:session-refreshed-at';

/** Renew well before the 15-minute access token expires. */
export const SESSION_STALE_MS = 10 * 60_000;
export const KEEP_ALIVE_CHECK_MS = 60_000;

let inFlight: Promise<RefreshOutcome> | null = null;

/** Remembers (for every tab) when the session was last renewed or started. */
export function markSessionRefreshed(at = Date.now()): void {
  try {
    globalThis.localStorage.setItem(LAST_REFRESH_KEY, String(at));
  } catch {
    // Blocked storage: keep-alive just renews more often.
  }
}

export function lastSessionRefresh(): number {
  try {
    return Number(globalThis.localStorage.getItem(LAST_REFRESH_KEY) ?? 0) || 0;
  } catch {
    return 0;
  }
}

export function isSessionStale(now = Date.now()): boolean {
  return now - lastSessionRefresh() >= SESSION_STALE_MS;
}

async function postRefresh(): Promise<RefreshOutcome> {
  let response: Response;
  try {
    response = await fetch(`${apiRuntime().baseUrl}/auth/refresh`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', [CSRF_HEADER]: CSRF_HEADER_VALUE },
    });
  } catch {
    return 'unavailable';
  }
  if (response.ok) return 'ok';
  return [400, 401, 403].includes(response.status) ? 'invalid' : 'unavailable';
}

async function withCrossTabLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = (globalThis.navigator as Navigator | undefined)?.locks;
  // `await`: some DOM typings wrap the callback's promise in another one.
  return locks ? await locks.request(REFRESH_LOCK, fn) : fn();
}

/** Renews the session once, however many callers ask at the same time. */
export function refreshSession(): Promise<RefreshOutcome> {
  if (!inFlight) {
    const requestedAt = Date.now();
    inFlight = withCrossTabLock(async () => {
      // Another tab rotated while this one waited for the lock: nothing left to do.
      if (lastSessionRefresh() >= requestedAt) return 'ok' as const;
      const outcome = await postRefresh();
      if (outcome === 'ok') markSessionRefreshed();
      return outcome;
    }).finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

/** Proactive renewal (keep-alive): only for a visible tab whose session is getting old. */
export async function keepSessionAliveIfStale(): Promise<RefreshOutcome | 'fresh'> {
  if (globalThis.document?.visibilityState === 'hidden') return 'fresh';
  if (!isSessionStale()) return 'fresh';
  return refreshSession();
}
